// Ficha de un contrato. En BORRADOR se edita; una vez enviado o firmado se
// muestra en modo lectura: lo que el cliente ya vio no se toca.
//
// El contrato es una FOTO: monto y cláusulas se copiaron al generarlo y acá
// se editan libremente sin tocar el presupuesto de origen.

import { api } from "../api";
import { esc } from "../html";
import { imprimirHtml, documentoImpreso } from "../imprimir";
import { iconoVolver, iconoMas, iconoCerrar } from "../iconos";
import { ETIQUETA_ESTADO_CONTRATO, claseEstadoContrato } from "../etiquetas";
import type { Contrato, ClausulaContrato, FichaContrato } from "../../lib/tipos";
import { formatearMonto, parsearMonto } from "../../lib/calculo";
import { formatoLocal } from "../../lib/fecha";

/** Nombre del PDF: proyecto si hay, si no el cliente. Igual que el presupuesto. */
function tituloDeLaHoja(c: Contrato): string {
  const de = c.proyecto_nombre || c.cliente_nombre;
  return de ? `Contrato ${c.numero} - ${de}` : `Contrato ${c.numero}`;
}

/** El papel. Mismo esqueleto y CSS que el presupuesto, con firmas al pie. */
function hojaImpresa(c: Contrato, clausulas: ClausulaContrato[]): string {
  const cuerpo = `
  <h1>Contrato de servicios N.º ${c.numero}</h1>
  <div class="gris">${formatoLocal(c.fecha)}</div>
  <div class="datos">
    <div><b>Cliente:</b> ${esc(c.cliente_nombre)}</div>
    ${c.proyecto_nombre ? `<div><b>Proyecto:</b> ${esc(c.proyecto_nombre)}</div>` : ""}
    ${c.presupuesto_numero ? `<div><b>Presupuesto de referencia:</b> Nº ${c.presupuesto_numero}</div>` : ""}
    <div><b>Precio pactado:</b> ${formatearMonto(c.monto, c.moneda)}</div>
    ${c.plazo_dias ? `<div><b>Plazo de entrega:</b> ${c.plazo_dias} días hábiles</div>` : ""}
    ${c.fecha_inicio ? `<div><b>Inicio:</b> ${formatoLocal(c.fecha_inicio)}</div>` : ""}
  </div>
  ${c.objeto ? `<div class="parrafo"><b>Objeto del contrato.</b> ${esc(c.objeto)}</div>` : ""}
  ${
    clausulas.length
      ? `<section class="condiciones">
           <h2>Cláusulas</h2>
           ${clausulas
             .map(
               (cl, i) => `<div class="clausula">
                  <b>${i + 1}. ${esc(cl.titulo)}</b>
                  <div>${esc(cl.texto)}</div>
                </div>`,
             )
             .join("")}
         </section>`
      : ""
  }
  ${c.notas ? `<div class="notas">${esc(c.notas)}</div>` : ""}
  <div class="firmas">
    <div class="firma">Firma del desarrollador<br />Aclaración y fecha</div>
    <div class="firma">Firma del cliente<br />Aclaración y fecha</div>
  </div>
  <footer>Contrato N.º ${c.numero} · ${esc(c.cliente_nombre)} · ${formatoLocal(c.fecha)}</footer>`;

  return documentoImpreso(esc(tituloDeLaHoja(c)), cuerpo);
}

function filaClausulaEditable(cl: { titulo: string; texto: string }, i: number): string {
  return `
    <div class="clausulaEd rounded-xl border border-borde bg-superficie p-3" data-i="${i}">
      <div class="flex items-start gap-2">
        <input class="cTitulo campo flex-1" maxlength="200" placeholder="Título"
               value="${esc(cl.titulo)}" />
        <button type="button" class="cQuitar mt-1 rounded-lg p-2 text-apagado transition hover:bg-realce hover:text-alerta"
                aria-label="Quitar cláusula">${iconoCerrar}</button>
      </div>
      <textarea class="cTexto campo mt-2" rows="3" maxlength="4000"
                placeholder="Texto de la cláusula">${esc(cl.texto)}</textarea>
    </div>`;
}

export async function renderContrato(
  contenedor: HTMLElement,
  params: Record<string, string>,
): Promise<void> {
  const id = Number(params.id);
  if (!Number.isInteger(id) || id <= 0) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">Ese contrato no existe.</div>`;
    return;
  }

  function leerClausulas(): { titulo: string; texto: string }[] {
    return [...contenedor.querySelectorAll<HTMLElement>(".clausulaEd")].map((el) => ({
      titulo: (el.querySelector(".cTitulo") as HTMLInputElement).value.trim(),
      texto: (el.querySelector(".cTexto") as HTMLTextAreaElement).value.trim(),
    }));
  }

  async function pintar(): Promise<void> {
    const f = await api.get<FichaContrato>(`/api/contratos/${id}`);
    const c = f.contrato;
    const editable = c.estado === "BORRADOR";

    const acciones: Record<string, string> = {};
    if (c.estado === "BORRADOR") acciones["ENVIADO"] = "Marcar enviado";
    if (c.estado === "ENVIADO") {
      acciones["FIRMADO"] = "Se firmó";
      acciones["BORRADOR"] = "Volver a borrador";
    }

    contenedor.innerHTML = `
      <a href="#/contratos" class="mb-3 inline-flex items-center gap-1 text-sm text-apagado hover:text-texto">
        ${iconoVolver} Contratos
      </a>

      <div class="flex items-start gap-2">
        <div class="min-w-0 flex-1">
          <h1 class="titulo-pagina">Contrato N.º ${c.numero}</h1>
          <p class="subtitulo mt-0.5">${esc(c.cliente_nombre)}${
            c.proyecto_nombre ? " · " + esc(c.proyecto_nombre) : ""
          }</p>
        </div>
        <span class="${claseEstadoContrato(c.estado)}">${ETIQUETA_ESTADO_CONTRATO[c.estado]}</span>
      </div>

      <p class="mt-1 text-xs text-apagado">
        ${formatoLocal(c.fecha)}${
          c.presupuesto_numero
            ? ` · del <a href="#/presupuesto/${c.presupuesto_id}" class="text-acento hover:underline">presupuesto Nº ${c.presupuesto_numero}</a>`
            : ""
        }${c.enviado_el ? ` · enviado el ${formatoLocal(c.enviado_el)}` : ""}${
          c.firmado_el ? ` · firmado el ${formatoLocal(c.firmado_el)}` : ""
        }</p>

      ${
        editable
          ? `<label class="mt-4 block"><span class="etiqueta">Objeto (qué se contrata)</span>
               <textarea id="objeto" class="campo" rows="3" maxlength="2000"
                         placeholder="Desarrollo de la web con tienda y panel de administración...">${esc(c.objeto ?? "")}</textarea></label>

             <div class="mt-3 grid grid-cols-2 gap-3">
               <label><span class="etiqueta">Monto (${c.moneda})</span>
                 <input id="monto" class="campo" inputmode="decimal"
                        value="${formatearMonto(c.monto, c.moneda)}" /></label>
               <label><span class="etiqueta">Plazo (días hábiles)</span>
                 <input id="plazo" type="number" class="campo" min="1" max="3650"
                        value="${c.plazo_dias ?? ""}" placeholder="Sin plazo" /></label>
             </div>
             <label class="mt-3 block"><span class="etiqueta">Fecha de inicio (opcional)</span>
               <input id="inicio" type="date" class="campo" value="${c.fecha_inicio ?? ""}" /></label>`
          : `<div class="tarjeta mt-4 space-y-1 text-sm">
               ${c.objeto ? `<p class="whitespace-pre-wrap">${esc(c.objeto)}</p>` : `<p class="text-apagado">Sin objeto.</p>`}
               <p class="pt-2"><b>Precio pactado:</b> ${formatearMonto(c.monto, c.moneda)}</p>
               ${c.plazo_dias ? `<p><b>Plazo:</b> ${c.plazo_dias} días hábiles</p>` : ""}
               ${c.fecha_inicio ? `<p><b>Inicio:</b> ${formatoLocal(c.fecha_inicio)}</p>` : ""}
             </div>`
      }

      <h2 class="mt-5 mb-2 text-sm font-semibold uppercase tracking-wider text-apagado">
        Cláusulas <span class="font-normal">(${f.clausulas.length})</span>
      </h2>
      ${
        editable
          ? `<div id="clausulas" class="space-y-2">
               ${f.clausulas.map((cl, i) => filaClausulaEditable(cl, i)).join("")}
             </div>
             <button id="bClausula" class="btn-sec mt-2 w-full">${iconoMas} Agregar cláusula</button>`
          : f.clausulas.length
            ? `<div class="space-y-2">${f.clausulas
                .map(
                  (cl, i) => `<div class="tarjeta !p-3 text-sm">
                     <b>${i + 1}. ${esc(cl.titulo)}</b>
                     <p class="mt-1 whitespace-pre-wrap text-apagado">${esc(cl.texto)}</p>
                   </div>`,
                )
                .join("")}</div>`
            : `<div class="tarjeta text-sm text-apagado">Sin cláusulas.</div>`
      }

      ${
        editable
          ? `<label class="mt-4 block"><span class="etiqueta">Notas</span>
               <textarea id="notas" class="campo" rows="2" maxlength="4000">${esc(c.notas ?? "")}</textarea></label>`
          : c.notas
            ? `<p class="mt-4 whitespace-pre-wrap text-sm text-apagado">${esc(c.notas)}</p>`
            : ""
      }

      <p id="err" class="mt-3 text-sm text-alerta"></p>

      <div class="mt-3 flex flex-wrap gap-2">
        ${editable ? `<button id="bGuardar" class="btn-primario flex-1">Guardar</button>` : ""}
        <button id="bImprimir" class="btn-sec flex-1">Imprimir / PDF</button>
      </div>

      <div class="mt-2 flex flex-wrap gap-2">
        ${Object.entries(acciones)
          .map(
            ([estado, texto]) =>
              `<button class="bEstado btn-sec flex-1" data-estado="${estado}">${texto}</button>`,
          )
          .join("")}
        ${
          c.estado !== "ANULADO"
            ? `<button id="bAnular" class="btn-sec text-apagado">Anular</button>`
            : ""
        }
      </div>`;

    const err = contenedor.querySelector("#err") as HTMLElement;

    if (editable) {
      contenedor.querySelector("#bClausula")?.addEventListener("click", () => {
        const lista = contenedor.querySelector("#clausulas") as HTMLElement;
        lista.insertAdjacentHTML(
          "beforeend",
          filaClausulaEditable({ titulo: "", texto: "" }, lista.children.length),
        );
        enganchesQuitar();
      });

      const enganchesQuitar = () => {
        contenedor.querySelectorAll<HTMLButtonElement>(".cQuitar").forEach((b) => {
          b.onclick = () => b.closest(".clausulaEd")?.remove();
        });
      };
      enganchesQuitar();

      contenedor.querySelector("#bGuardar")?.addEventListener("click", async () => {
        err.textContent = "";
        const monto = parsearMonto(
          (contenedor.querySelector("#monto") as HTMLInputElement).value,
        );
        if (monto === null) {
          err.textContent = "No entiendo el monto. Probá algo como 25.000 o 25000,50";
          return;
        }
        const plazo = (contenedor.querySelector("#plazo") as HTMLInputElement).value;
        const inicio = (contenedor.querySelector("#inicio") as HTMLInputElement).value;
        const clausulas = leerClausulas();
        if (clausulas.some((cl) => !cl.titulo || !cl.texto)) {
          err.textContent = "Hay cláusulas sin título o sin texto. Completalas o quitalas.";
          return;
        }
        try {
          await api.put(`/api/contratos/${id}`, {
            objeto:
              (contenedor.querySelector("#objeto") as HTMLTextAreaElement).value.trim() || null,
            monto,
            plazo_dias: plazo ? Number(plazo) : null,
            fecha_inicio: inicio || null,
            notas: (contenedor.querySelector("#notas") as HTMLTextAreaElement).value.trim() || null,
            clausulas,
          });
          await pintar();
        } catch (e) {
          err.textContent = e instanceof Error ? e.message : "No se pudo guardar";
        }
      });
    }

    contenedor.querySelector("#bImprimir")?.addEventListener("click", async () => {
      // Se imprime lo que está guardado, no lo que hay en pantalla sin guardar.
      const actual = await api.get<FichaContrato>(`/api/contratos/${id}`);
      imprimirHtml(hojaImpresa(actual.contrato, actual.clausulas), tituloDeLaHoja(actual.contrato));
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bEstado").forEach((b) => {
      b.addEventListener("click", async () => {
        err.textContent = "";
        try {
          await api.post(`/api/contratos/${id}/estado`, { estado: b.dataset.estado });
          await pintar();
        } catch (e) {
          err.textContent = e instanceof Error ? e.message : "No se pudo cambiar el estado";
        }
      });
    });

    contenedor.querySelector("#bAnular")?.addEventListener("click", async () => {
      if (!confirm("¿Anular el contrato? Queda guardado, pero no se puede reactivar.")) return;
      await api.del(`/api/contratos/${id}`);
      await pintar();
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudo cargar el contrato: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
